import { useMemo, type ReactElement } from 'react'
import { Grid, styled, Typography, Button, Skeleton } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import CardContainer from '@/components/dashboard/CardContainer'
import QrCodeButton from '@/components/sidebar/QrCodeButton'
import ModalButton from '@/components/common/ModalButton'
import { useAppSelector } from '@/store'
import { selectCurrency } from '@/store/settingsSlice'
import { formatCurrency } from '@/utils/formatNumber'
import { useVisibleBalances } from '@/hooks/useVisibleBalances'
import SendAssets from '@/components/dashboard/SendAssets/SendAssets'
import useInheritance from '@/hooks/useInheritance'
import SendProcessing from '@/components/inheritors/SendProcessing'
import SendIcon from '@mui/icons-material/Send'
import QrCodeRoundedIcon from '@mui/icons-material/QrCodeRounded'
import GroupWorkIcon from '@mui/icons-material/GroupWork'
import { RuleType } from '@/store/setUpInheritorsSlice'
import InheritorsIcon from '@/public/images/apps/view-inheritance.svg'
import { INHERIT_EVENTS, trackEvent } from '@/services/analytics'
import useDesktop from '@/hooks/useDesktop'
// import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined'

const Btn = styled(Button)(`
    height: 48px;
    margin-top: 16px;
    margin-bottom: 8px;
    font-size: 14px;
    font-style: normal;
    font-weight: bold;
    line-height: 160%;
    margin-right: 16px;
    border-radius: 4px;
    border: 1px solid var(--Emerald-600, #22C55E);
    margin: 0;
`)

const ValueSkeleton = () => <Skeleton variant="text" width={150} />

const TotalAssetBalance = (): ReactElement => {
  const router = useRouter()
  const isDesktop = useDesktop()
  const currency = useAppSelector(selectCurrency)
  const { balances, loading: balancesLoading } = useVisibleBalances()
  const inheritance = useInheritance()

  const fiatTotal = useMemo(
    () => (balances.fiatTotal ? formatCurrency(balances.fiatTotal, currency) : ''),
    [currency, balances.fiatTotal],
  )

  const noInheritance = inheritance.rule.type === RuleType.Unknown

  return (
    <CardContainer title={'Total balance'}>
      <Grid container px={'16px'}>
        <Grid item xs={12}>
          <Typography
            sx={{
              color: 'var(--Ebony-Clay-500, #7C8AB4)',
              fontSize: '14px',
              fontWeight: '400',
              fontStyle: 'normal',
              lineHeight: '160%',
            }}
          >
            Total asset value
          </Typography>
        </Grid>
        <Grid item xs={12} mt={'4px'}>
          <Typography
            sx={{
              color: 'var(--Ebony-Clay-300, #D2D7E5)',
              fontSize: '32px',
              fontWeight: '600',
              fontStyle: 'normal',
              lineHeight: '120%',
            }}
          >
            {balancesLoading ? (
              <ValueSkeleton />
            ) : (
              <>
                ${fiatTotal.replace('USD', '')}{' '}
                <span
                  style={{
                    color: 'var(--Ebony-Clay-700, #485581)',
                    fontWeight: '500',
                    lineHeight: '120%',
                    fontSize: '16px',
                  }}
                >
                  USD
                </span>
              </>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1.5} flexWrap="wrap" mt={'8px'}>
            <Grid item xs={12} sm="auto">
              <Link
                href={{
                  pathname: noInheritance ? 'set-up-inheritance' : 'inheritance-management',
                  query: { wallet: router.query.wallet },
                }}
                passHref
              >
                <Btn
                  onClick={() => {
                    trackEvent({
                      ...INHERIT_EVENTS.CLICK_SET_UP_INHERITANCE,
                      ...{ label: noInheritance ? 'Set up inheritance' : 'Manage inheritance' },
                    })
                  }}
                  variant="contained"
                  sx={{
                    background: 'var(--Emerald-600, #22C55E)',
                    color: 'var(--Supporting-Dark-BG-900, #171A26)',
                  }}
                  startIcon={
                    <>
                      {!noInheritance && (
                        <InheritorsIcon
                          sx={{
                            fontSize: '18px !important',
                          }}
                        />
                      )}
                      {noInheritance && (
                        <GroupWorkIcon
                          sx={{
                            fontSize: '24px !important',
                          }}
                        />
                      )}
                    </>
                  }
                  fullWidth
                >
                  {noInheritance ? 'Set up inheritance' : 'Manage inheritance'}
                </Btn>
              </Link>
            </Grid>
            <Grid item xs={6} sm="auto">
              <ModalButton
                titleModal={'Send assets'}
                contentModal={
                  <SendProcessing>
                    <SendAssets />
                  </SendProcessing>
                }
              >
                <Btn
                  onClick={() => trackEvent(INHERIT_EVENTS.CLICK_SEND)}
                  startIcon={
                    <SendIcon
                      sx={{
                        fontSize: '24px !important',
                      }}
                    />
                  }
                  fullWidth
                >
                  Send
                </Btn>
              </ModalButton>
            </Grid>
            <Grid item xs={6} sm="auto">
              <QrCodeButton>
                <Btn
                  onClick={() => trackEvent(INHERIT_EVENTS.CLICK_RECEIVE)}
                  startIcon={
                    <QrCodeRoundedIcon
                      sx={{
                        fontSize: '24px !important',
                      }}
                    />
                  }
                  fullWidth
                >
                  Receive
                </Btn>
              </QrCodeButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardContainer>
  )
}

export default TotalAssetBalance
